import React, { useRef, useMemo, useEffect } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import Link from '@components/atoms/link/link';
import useWindowSize from '@hooks/use-window-size';
import useStickyNavQuery from '@hooks/sticky-nav/use-sticky-nav-query';
import useStickyNavNavigation from '@hooks/sticky-nav/use-sticky-nav-navigation';
import useStickyNavRouterUrlSync from '@hooks/sticky-nav/use-sticky-nav-router-url-sync';
import useScrollToActiveItem from '@hooks/sticky-nav/use-scroll-to-active-item';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import {
  getActiveEntryPoint,
  getStickyEntryPoints,
  getEffectiveAsPath,
} from '@helpers/utils/sticky-nav/sticky-nav-entry-point-helpers';
import { isFromStickyNav } from '@helpers/utils/sticky-nav/sticky-nav-utils';
import { isHomePage } from '@helpers/routing';
import ConditionalWrapper from '@components/common/conditional-wrapper/conditional-wrapper';
import styles from './sticky-nav-entry-point.module.scss';

interface StickyNavEntryPointProps {
  className?: string;
  universe?: number;
}

const StickyNavEntryPoint: React.FC<StickyNavEntryPointProps> = ({ className, universe }) => {
  const router = useRouter();
  const { t: tA11y } = useTranslation('a11y');
  const { isWiderThanMd, hasWindowSizeBeenSet } = useWindowSize();
  const containerRef = useRef<HTMLDivElement>(null);
  const activeItemRefs = useRef<Map<string, HTMLLIElement>>(new Map());
  const hasTrackedViewRef = useRef<boolean>(false);

  const { currentUrl } = useStickyNavRouterUrlSync();

  const { stickyNavData, currentUniverse } = useStickyNavQuery({
    universe,
  });

  const entryPoints = useMemo(
    () => getStickyEntryPoints(stickyNavData, currentUniverse),
    [stickyNavData, currentUniverse],
  );

  const effectiveAsPath = useMemo(
    () => getEffectiveAsPath(currentUrl, router.asPath || ''),
    [currentUrl, router.asPath],
  );

  const activeEntryPoint = useMemo(
    () =>
      getActiveEntryPoint({
        routerPathname: router.pathname,
        routerAsPath: effectiveAsPath,
        entryPoints,
        currentUniverse,
      }),
    [router.pathname, effectiveAsPath, entryPoints, currentUniverse],
  );

  const { handleEntryPointClick, buildLinkHref } = useStickyNavNavigation();

  const { sendAnalyticEvent, scriptsEventData } = useAnalyticEvents('navigation');

  useScrollToActiveItem({
    activeItemId: activeEntryPoint,
    containerRef,
    itemRefs: activeItemRefs,
    items: entryPoints,
  });

  const shouldHide = hasWindowSizeBeenSet && isWiderThanMd;

  useEffect(() => {
    if (shouldHide || !entryPoints || entryPoints.length === 0) {
      return;
    }

    if (!scriptsEventData?.isTcEvents1Loaded || !scriptsEventData?.isTcEvents7Loaded) {
      return;
    }

    if (hasTrackedViewRef.current) {
      return;
    }
    hasTrackedViewRef.current = true;

    sendAnalyticEvent('view_sticky_navigation', {
      label: 'sticky_navigation',
    });
  }, [
    entryPoints?.length,
    shouldHide,
    router.pathname,
    router.asPath,
    scriptsEventData?.isTcEvents1Loaded,
    scriptsEventData?.isTcEvents7Loaded,
  ]);

  if (shouldHide) {
    return null;
  }

  if (!entryPoints || entryPoints.length === 0) {
    return null;
  }

  return (
    <nav
      className={clsx(styles.stickyNav, className)}
      role="navigation"
      aria-label={tA11y('A11Y.STICKY_NAV.LABEL')}
    >
      <div
        ref={containerRef}
        className={styles.stickyNav__container}
      >
        <ul className={styles.stickyNav__list}>
          {entryPoints.map((entryPoint, index) => {
            const isActive = activeEntryPoint === entryPoint.id;
            const shouldUseH1 =
              isActive && isFromStickyNav(router.query ?? {}) && !isHomePage(router.pathname, router.asPath);

            return (
              <li
                key={entryPoint.id}
                ref={(el) => {
                  if (el) {
                    activeItemRefs.current.set(entryPoint.id, el);
                  } else {
                    activeItemRefs.current.delete(entryPoint.id);
                  }
                }}
                className={styles.stickyNav__item}
              >
                <ConditionalWrapper
                  condition={shouldUseH1}
                  wrap={(children) => <h1 className={styles.stickyNav__heading}>{children}</h1>}
                >
                  <Link
                    href={buildLinkHref(entryPoint)}
                    className={clsx(styles.stickyNav__button, isActive && styles['stickyNav__button--active'])}
                    onClick={() => handleEntryPointClick(entryPoint, index + 1)}
                    aria-current={isActive ? 'page' : undefined}
                    data-cy={`sticky-nav-${entryPoint.mnemonic}`}
                    data-vc-dd-action-name={`sticky_nav_${entryPoint.mnemonic}`}
                  >
                    {entryPoint.title}
                  </Link>
                </ConditionalWrapper>
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
};

export default StickyNavEntryPoint;
